/**
 * Environment Variables Schema with Zod Validation
 *
 * Provides runtime validation and type-safe access to environment variables.
 * All VITE_ prefixed variables are exposed to the frontend bundle.
 */

import { z } from 'zod';

/**
 * Environment variable schema
 * Validates at runtime that all required environment variables are present and valid
 */
const envSchema = z.object({
  // Application Metadata
  VITE_APP_NAME: z.string().default('Equinox'),
  VITE_APP_DESCRIPTION: z.string().default('User Console for Neutrino Platform'),
  VITE_APP_URL: z.string().url().default('https://app.staging.equinox.com.my'),
  VITE_APP_SOCIAL_IMAGE: z.string().default('/images/neutrino-console.png'),

  // Authentication (Required)
  VITE_CLERK_PUBLISHABLE_KEY: z.string()
    .min(1, 'Clerk publishable key is required')
    .refine(
      (key) => key.startsWith('pk_test_') || key.startsWith('pk_live_'),
      'Clerk key must start with pk_test_ or pk_live_'
    ),

  // API Configuration
  VITE_API_URL: z.string().url().default('https://api.staging.equinox.com.my'),
  VITE_PAYMENT_SERVICE_URL: z.string().url().optional(),
  VITE_INSURANCE_SERVICE_URL: z.string().url().optional(),
  VITE_BILLING_SERVICE_URL: z.string().url().optional(),
  VITE_WEBHOOK_SERVICE_URL: z.string().url().optional(),

  // Portal Configuration
  VITE_PORTAL_URL: z.string().url().optional(),

  // Development Mode (auto-detected)
  DEV: z.boolean().default(false),
  PROD: z.boolean().default(false),
  MODE: z.enum(['development', 'staging', 'production']).default('development'),
});

/**
 * Validated environment variables
 * TypeScript type inferred from the Zod schema
 */
export type Env = z.infer<typeof envSchema>;

/**
 * Parse and validate environment variables
 * @throws {ZodError} if validation fails
 */
function validateEnv(): Env {
  const rawEnv = {
    VITE_APP_NAME: import.meta.env.VITE_APP_NAME,
    VITE_APP_DESCRIPTION: import.meta.env.VITE_APP_DESCRIPTION,
    VITE_APP_URL: import.meta.env.VITE_APP_URL,
    VITE_APP_SOCIAL_IMAGE: import.meta.env.VITE_APP_SOCIAL_IMAGE,
    VITE_CLERK_PUBLISHABLE_KEY: import.meta.env.VITE_CLERK_PUBLISHABLE_KEY,
    VITE_API_URL: import.meta.env.VITE_API_URL,
    VITE_PAYMENT_SERVICE_URL: import.meta.env.VITE_PAYMENT_SERVICE_URL,
    VITE_INSURANCE_SERVICE_URL: import.meta.env.VITE_INSURANCE_SERVICE_URL,
    VITE_BILLING_SERVICE_URL: import.meta.env.VITE_BILLING_SERVICE_URL,
    VITE_WEBHOOK_SERVICE_URL: import.meta.env.VITE_WEBHOOK_SERVICE_URL,
    VITE_PORTAL_URL: import.meta.env.VITE_PORTAL_URL,
    DEV: import.meta.env.DEV,
    PROD: import.meta.env.PROD,
    MODE: import.meta.env.MODE,
  };

  try {
    return envSchema.parse(rawEnv);
  } catch (error) {
    if (error instanceof z.ZodError) {
      console.error('❌ Environment variable validation failed:');
      console.error(error.format());

      // Provide helpful error messages
      const missingVars = error.issues
        .filter(issue => issue.code === 'invalid_type' && issue.received === 'undefined')
        .map(issue => issue.path.join('.'));

      if (missingVars.length > 0) {
        console.error('\n📝 Missing required environment variables:');
        missingVars.forEach(varName => {
          console.error(`   - ${varName}`);
        });
        console.error('\n💡 Check your .env file or deployment configuration.');
      }

      throw new Error('Environment validation failed. See console for details.');
    }
    throw error;
  }
}

/**
 * Validated and typed environment variables
 * Safe to use throughout the application
 */
export const env = validateEnv();

/**
 * Helper function to check if we're in development mode
 */
export const isDevelopment = env.MODE === 'development' || env.DEV;

/**
 * Helper function to check if we're in production mode
 */
export const isProduction = env.MODE === 'production' || env.PROD;

/**
 * Helper function to check if we're in staging mode
 */
export const isStaging = env.MODE === 'staging';

/**
 * Helper to get the Clerk publishable key (for backward compatibility)
 */
export const getClerkPublishableKey = () => env.VITE_CLERK_PUBLISHABLE_KEY;

/**
 * Helper to get the API URL (for backward compatibility)
 */
export const getApiUrl = () => env.VITE_API_URL;

/**
 * Helper to get the payment service URL (for backward compatibility)
 */
export const getPaymentServiceUrl = () => env.VITE_PAYMENT_SERVICE_URL;

/**
 * Helper to get the insurance service URL
 */
export const getInsuranceServiceUrl = () => env.VITE_INSURANCE_SERVICE_URL;

/**
 * Helper to get the billing service URL
 */
export const getBillingServiceUrl = () => env.VITE_BILLING_SERVICE_URL;

/**
 * Helper to get the webhook service URL
 */
export const getWebhookServiceUrl = () => env.VITE_WEBHOOK_SERVICE_URL;

/**
 * Helper to get the portal URL (for backward compatibility)
 */
export const getPortalUrl = () => env.VITE_PORTAL_URL;
